// api.ts — the only file that knows the server's URL shapes. Every UI panel calls these.

export interface Rules {
  photoMime?: string[];
  videoMime?: string[];
  videoMaxSecondsFree: number;
  videoMaxSecondsPaid: number;
  uploadMaxBytes: number;
  lilypadsPerDay: number;
  priceMonthlyUsd: number;
  priceYearlyUsd: number;
  emotes: string[];
  carriers: string[];
  ssoEnabled: boolean;
  paypalConfigured: boolean;
  paypalClientId: string | null;
  paypalPlanMonthly: string | null;
  paypalPlanYearly: string | null;
}

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_e164: string;
  tier: 'free' | 'premium' | 'lifetime' | 'admin';
  email_verified_at: string | null;
  phone_verified_at: string | null;
}

export interface Entitlements {
  tier: User['tier'];
  subscriptionActive: boolean;
  paid: boolean;
  canUpload: boolean;
  canCreateLilypad: boolean;
  maxVideoSeconds: number;
  lilypadsCreatedToday: number;
  lilypadsPerDay: number;
  nextLilypadAllowedAt: string | null;
}

export interface Lilypad {
  cover_media_id?: string | null;
  cover_style?: 'classic' | 'blossom' | 'evening';
  guest_contribution?: 'contributor' | 'viewer';
  can_manage?: boolean;
  can_contribute?: boolean;
  id: string;
  slug: string;
  title: string;
  description: string;
  event_date: string;
  pos_x: number;
  pos_y: number;
  size: number;
  created_at: string;
  mine: boolean;
  role: 'owner' | 'member';
  owner_name: string;
  owner_id: string;
  pond_id: string;
  media_count: number;
  member_count: number;
  url: string;
}

export interface Media {
  id: string;
  kind: 'photo' | 'video';
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  caption: string;
  created_at: string;
  has_thumb: boolean;
  rendition_state: 'pending' | 'ready' | 'failed';
  display_bytes: number | null;
  uploader_id: string;
  uploader_name: string;
  comment_count: number;
  reactions: Array<{ emote: string; n: number }>;
}

export interface Leaf {
  id: string;
  title: string;
  created_at: string;
  creator_name: string;
  item_count: number;
  cover_media_id: string | null;
}

/** Public, whitelisted pond theme (server routes/ponds.ts publicTheme). `character` is set only after the person consents. */
export interface PondTheme { character?: 'lily' | null; characterStyle?: Record<string, unknown> | null }

/** Inactivity retention (server services/retention.ts). */
export interface Retention { confirmedAt: string; deleteAt: string | null; daysLeft: number | null; exempt: boolean; needsConfirm: boolean }

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public body: unknown) {
    super(message);
  }
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
    body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(res.status, json.error ?? 'error', json.message ?? json.error ?? res.statusText, json);
  return json as T;
}

export const api = {
  rules: () => call<Rules>('GET', '/api/rules'),
  resolve: (slug?: string) =>
    call<{ pondId: string | null; lilypadSlug: string | null; baseDomain: string; pondTheme?: PondTheme | null; lilypad: { id: string; slug: string; title: string; event_date: string; owner_first: string } | null }>(
      'GET',
      `/api/resolve${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`,
    ),
  me: () => call<{ user: User; entitlements: Entitlements; retention: Retention | null }>('GET', '/api/me'),
  confirmContact: (email: string, phone: string) => call<{ ok: true; confirmedAt: string }>('POST', '/api/me/confirm-contact', { email, phone }),
  exportSummary: () => call<{ uploads: number; bytes: number }>('GET', '/api/me/export/summary'),
  exportUrl: '/api/me/export',
  register: (b: Record<string, unknown>) => call<{ status: string; userId: string; lilypadId: string }>('POST', '/api/auth/register', b),
  verify: (channel: 'email' | 'sms', code: string) => call<{ status: string; user: User }>('POST', '/api/auth/verify', { channel, code }),
  resend: (channel: 'email' | 'sms', carrier?: string | null) => call<{ ok: true }>('POST', '/api/auth/resend', { channel, carrier }),
  login: (email: string, password: string) => call<{ user: User; entitlements: Entitlements }>('POST', '/api/auth/login', { email, password }),
  logout: () => call<{ ok: true }>('POST', '/api/auth/logout'),

  pond: () => call<{ pond: { id: string; name: string; theme?: PondTheme } | null; baseDomain: string; lilypads: Lilypad[] }>('GET', '/api/pond'),
  lilypad: (id: string) => call<{ lilypad: Lilypad & { owner_id: string }; members: Array<{ id: string; first_name: string; last_name: string; role: string }>; codes: Array<{ id: string; label: string; active: boolean; uses: number }>; isOwner: boolean }>('GET', `/api/lilypads/${id}`),
  createLilypad: (b: { slug: string; title: string; description: string; eventDate: string; joinCode: string; codeLabel?: string; pondId?: string; coverStyle?: Lilypad['cover_style']; guestContribution?: 'contributor' | 'viewer' }) =>
    call<{ id: string; slug: string; url: string }>('POST', '/api/lilypads', b),
  joinLilypad: (id: string, joinCode: string) => call<{ ok: true }>('POST', `/api/lilypads/${id}/join`, { joinCode }),
  addCode: (id: string, joinCode: string, label: string) => call<{ id: string }>('POST', `/api/lilypads/${id}/codes`, { joinCode, label }),
  deactivateCode: (id: string, codeId: string) => call<{ ok: true }>('POST', `/api/lilypads/${id}/codes/${codeId}/deactivate`),

  media: (lilypadId: string, options: { cursor?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (options.cursor) query.set('cursor', options.cursor);
    if (options.limit) query.set('limit', String(options.limit));
    return call<{ media: Media[]; nextCursor?: string | null; hasMore?: boolean }>('GET', '/api/lilypads/' + lilypadId + '/media' + (query.size ? '?' + query : ''));
  },
  uploadReceipt: (lilypadId: string, clientUploadId: string) => call<{ media: Media | null; status: 'received' | 'missing' }>('GET', '/api/lilypads/' + lilypadId + '/uploads/' + clientUploadId),
  updateLilypadCover: (id: string, b: { coverMediaId: string | null; coverStyle?: Lilypad['cover_style'] }) => call<{ ok: true }>('PATCH', '/api/lilypads/' + id + '/cover', b),
  deleteMedia: (id: string) => call<{ ok: true }>('DELETE', `/api/media/${id}`),
  thumbUrl: (id: string) => `/api/media/${id}/thumb`,
  /** Display (compressed) by default; `full` = the original, which needs a paid seat (server returns 402 otherwise). */
  fileUrl: (id: string, opts: { download?: boolean; full?: boolean } = {}) => {
    const q = [opts.download ? 'download=1' : '', opts.full ? 'quality=full' : ''].filter(Boolean).join('&');
    return `/api/media/${id}/file${q ? `?${q}` : ''}`;
  },

  leafs: (lilypadId: string) => call<{ leafs: Leaf[] }>('GET', `/api/lilypads/${lilypadId}/leafs`),
  createLeaf: (lilypadId: string, title: string) => call<{ leaf: Leaf }>('POST', `/api/lilypads/${lilypadId}/leafs`, { title }),
  leaf: (id: string) => call<{ leaf: Leaf & { lilypad_id: string }; items: Array<{ id: string; media_id: string; x: number; y: number; scale: number; rotation: number; z: number; kind: string }> }>('GET', `/api/leafs/${id}`),
  addLeafItem: (leafId: string, b: { mediaId: string; x?: number; y?: number; scale?: number; rotation?: number }) => call<{ item: unknown }>('POST', `/api/leafs/${leafId}/items`, b),
  removeLeafItem: (leafId: string, itemId: string) => call<{ ok: true }>('DELETE', `/api/leafs/${leafId}/items/${itemId}`),

  comments: (kind: 'media' | 'leaf' | 'lilypad', id: string) =>
    call<{ reactions?: Array<{ emote: string; n: number }>; comments: Array<{ id: string; parent_id: string | null; body: string; deleted?: boolean; created_at: string; user_id: string; author_name: string; reactions: Array<{ emote: string; n: number }> }> }>('GET', `/api/${kind}/${id}/comments`),
  comment: (kind: 'media' | 'leaf' | 'lilypad', id: string, body: string, parentId?: string) => call<{ comment: unknown }>('POST', `/api/${kind}/${id}/comments`, { body, parentId }),
  react: (kind: 'media' | 'leaf' | 'comment' | 'lilypad', id: string, emote: string) => call<{ reacted: boolean; reactions: Array<{ emote: string; n: number }> }>('POST', `/api/${kind}/${id}/react`, { emote }),

  threads: () => call<{ threads: Array<{ user_id: string; name: string; last_at: string; unread: number }> }>('GET', '/api/messages'),
  thread: (userId: string) => call<{ messages: Array<{ id: string; from_user: string; to_user: string; body: string; created_at: string }> }>('GET', `/api/messages/${userId}`),
  send: (userId: string, body: string) => call<{ message: unknown }>('POST', `/api/messages/${userId}`, { body }),

  billing: () => call<{ configured: boolean; mode: string; subscriptions: Array<{ plan: string; status: string; current_period_end: string | null }>; entitlements: Entitlements }>('GET', '/api/billing'),
  subscribe: (subscriptionId: string) => call<{ status: string; entitlements: Entitlements }>('POST', '/api/billing/subscribe', { subscriptionId }),

  shares: (pondId: string) => call<{ shares: Array<{ id: string; grantee_id: string; grantee_name: string; scope: 'pond' | 'lilypad'; lilypad_id: string | null; lilypad_title: string | null }> }>('GET', `/api/ponds/${pondId}/shares`),
  addShare: (pondId: string, b: { email: string; scope: 'pond' | 'lilypad'; lilypadId?: string }) => call<{ id: string }>('POST', `/api/ponds/${pondId}/shares`, b),
  removeShare: (pondId: string, shareId: string) => call<{ ok: true }>('DELETE', `/api/ponds/${pondId}/shares/${shareId}`),
  requestCreate: (pondId: string, message: string) => call<{ id: string }>('POST', `/api/ponds/${pondId}/create-requests`, { message }),
  createRequests: () => call<{ incoming: Array<{ id: string; pond_id: string; requester_name: string; message: string; status: string }>; outgoing: Array<{ id: string; pond_name: string; status: string }> }>('GET', '/api/create-requests'),
  decideRequest: (id: string, decision: 'approved' | 'denied') => call<{ ok: true }>('POST', `/api/create-requests/${id}/decide`, { decision }),

  /** XHR so we get upload progress. */
  upload(lilypadId: string, file: File, caption: string, onProgress: (frac: number) => void, options: { caption?: string; clientUploadId?: string; signal?: AbortSignal } = {}): Promise<{ media: Media; replayed?: boolean }> {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append('caption', options.caption ?? caption);
      fd.append('file', file, file.name);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/lilypads/${lilypadId}/media`);
      if (options.clientUploadId) xhr.setRequestHeader('X-Client-Upload-Id', options.clientUploadId);
      const cancel = () => xhr.abort();
      const cleanup = () => options.signal?.removeEventListener('abort', cancel);
      if (options.signal?.aborted) { reject(new DOMException('Upload stopped on this device.', 'AbortError')); return; }
      options.signal?.addEventListener('abort', cancel, { once: true });
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () => {
        cleanup();
        let j: Record<string, unknown> = {};
        try { j = JSON.parse(xhr.responseText); } catch { /* empty */ }
        if (xhr.status >= 200 && xhr.status < 300) resolve(j as unknown as { media: Media });
        else reject(new ApiError(xhr.status, String(j.error ?? 'error'), String(j.message ?? j.error ?? xhr.statusText), j));
      };
      xhr.onerror = () => { cleanup(); reject(new ApiError(0, 'network', 'The connection was lost. Check the upload receipt before retrying.', null)); };
      xhr.onabort = () => { cleanup(); reject(new DOMException('Upload stopped on this device. Server receipt may still be pending.', 'AbortError')); };
      xhr.send(fd);
    });
  },
};

/** Read a video's duration in the browser before uploading so people can trim first. */
export function probeVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    const url = URL.createObjectURL(file);
    const done = (d: number | null) => { URL.revokeObjectURL(url); resolve(d); };
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
    v.onerror = () => done(null);
    v.src = url;
  });
}
